// =============================================================================
// SIREPPAR - "Hay un archivo nuevo desde la última revisión"
// =============================================================================
// Una sección aprobada se queda verde para siempre, aunque el partido suba algo
// después. Eso hace que la supervisora tenga que acordarse de mirar: no hay nada en
// pantalla que le diga "acá entró algo nuevo".
//
// No hace falta una columna para saberlo. La sección guarda CUÁNDO se la revisó
// (`analisis_revisado_en` / `admisibilidad_revisado_en`) y cada documento guarda
// cuándo se cargó, así que el aviso sale de comparar las dos fechas.
//
// `actualizado_en` de la sección NO sirve para esto: es `@updatedAt`, se mueve con
// cualquier escritura sobre la fila (aprobar, anotar, eximir) y nunca con la carga
// de un archivo, que escribe en `documentos`.
//
// El aviso exige que el archivo nuevo siga PENDIENTE. Así desaparece solo apenas
// alguien lo revisa —aprobándolo o rechazándolo—, que es cuando deja de ser algo
// que se le pasó por alto.
// =============================================================================

interface ArchivoConFecha {
  creadoEn: string | Date;
  /** Estado en la dimensión que se está mirando. */
  estado: string;
  /** Cuándo se revisó ESTE archivo, si se revisó. */
  revisadoEn?: string | Date | null;
}

/**
 * Archivos cargados DESPUÉS de que se revisó la sección y que todavía nadie miró.
 * Devuelve 0 si la sección nunca se revisó: ahí no hay "novedad", está todo por hacer.
 */
export function archivosNuevosSinRevisar(
  revisadoEn: string | Date | null | undefined,
  archivos: ArchivoConFecha[],
): number {
  // El corte es la ÚLTIMA vez que alguien revisó algo en la sección: puede ser la
  // aprobación de la sección entera o la revisión de un archivo suelto.
  //
  // Al principio sólo se miraba la fecha de la sección, y eso dejaba fuera el caso
  // más común: la supervisora va revisando archivo por archivo sin aprobar la
  // sección todavía, llega uno nuevo y no se enteraba.
  const fechas = [
    revisadoEn ? new Date(revisadoEn).getTime() : 0,
    ...archivos.map((a) => (a.revisadoEn ? new Date(a.revisadoEn).getTime() : 0)),
  ];
  const corte = Math.max(...fechas);
  // Nadie revisó nada todavía: no hay "novedad" que señalar, está todo por hacer y
  // el contador de la sección ya lo dice.
  if (corte === 0) return 0;
  return archivos.filter(
    (a) => a.estado === 'PENDIENTE' && new Date(a.creadoEn).getTime() > corte,
  ).length;
}

/** Texto del aviso. Null cuando no hay nada nuevo. */
export function avisoArchivoNuevo(n: number): string | null {
  if (n <= 0) return null;
  return n === 1
    ? 'Se cargó 1 archivo nuevo después de revisar esta sección'
    : `Se cargaron ${n} archivos nuevos después de revisar esta sección`;
}
